import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, AlertCircle, ArrowLeftToLine, Utensils, GraduationCap, PlayCircle, Zap } from "lucide-react";
import { FurtherReading } from "@/components/FurtherReading";

export const fmt0 = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());
export const fmt1 = (n) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(1));

const bandBorder = (band) =>
  band === "correct"
    ? "border-emerald-300 bg-emerald-50/50"
    : band === "partial"
    ? "border-amber-300 bg-amber-50/50"
    : "border-red-300 bg-red-50/50";

export const NumberResult = ({ band, correctText, testid }) => {
  if (!band) return null;
  const map = {
    correct: { Icon: CheckCircle2, cls: "text-emerald-600", label: "Correct" },
    partial: { Icon: AlertCircle, cls: "text-amber-600", label: "Close — within 20%" },
    wrong: { Icon: XCircle, cls: "text-red-600", label: "Check again" },
  };
  const { Icon, cls, label } = map[band];
  return (
    <span className={`flex items-center gap-1.5 text-xs font-bold ${cls}`} data-testid={testid}>
      <Icon className="w-4 h-4" /> {label}
      {band !== "correct" && correctText && (
        <span className="font-mono-clinical font-semibold ml-1">→ {correctText}</span>
      )}
    </span>
  );
};

// A labelled numeric input with an optional "use calc" autofill and a result badge.
export const NumberField = ({
  label,
  unit,
  hint,
  value,
  onChange,
  autofillValue,
  onAutofill,
  result,
  correctText,
  disabled,
  testid,
}) => (
  <div className={`border-2 rounded-md p-3.5 transition-colors ${result ? bandBorder(result.band) : "border-slate-200"}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <label htmlFor={testid} className="text-sm font-semibold text-slate-800">
        {label}
      </label>
      {result && <NumberResult band={result.band} correctText={correctText} testid={`${testid}-result`} />}
    </div>
    {hint && <div className="text-[11px] text-slate-500 font-mono-clinical mt-0.5">{hint}</div>}
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1.5">
        <Input
          id={testid}
          type="number"
          step="any"
          min="0"
          inputMode="decimal"
          placeholder="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testid}
          className="w-32 font-mono-clinical"
        />
        <span className="text-sm text-slate-500 whitespace-nowrap">{unit}</span>
      </div>
      {onAutofill && (
        <button
          type="button"
          onClick={() => onAutofill(autofillValue)}
          disabled={disabled || autofillValue == null || isNaN(parseFloat(autofillValue))}
          data-testid={`${testid}-autofill`}
          className="flex items-center gap-1 text-xs font-semibold text-[#0047AB] border border-[#0047AB]/30 rounded-md px-2 py-1.5 hover:bg-[#0047AB]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Drop the calculated value into this field"
        >
          <ArrowLeftToLine className="w-3.5 h-3.5" /> Use calc result
        </button>
      )}
    </div>
  </div>
);

// A single-choice list of option cards. `value` is the selected index.
export const OptionList = ({ options, value, onPick, testidPrefix, revealed }) => (
  <div className="grid grid-cols-1 gap-2">
    {options.map((o, i) => {
      const selected = value === i;
      let cls = "border-slate-200 bg-white hover:border-[#0047AB]/50";
      if (revealed) {
        if (o.score >= 3) cls = "border-emerald-300 bg-emerald-50/60";
        else if (selected) cls = "border-red-300 bg-red-50/60";
        else cls = "border-slate-200 bg-white opacity-70";
      } else if (selected) {
        cls = "border-[#0047AB] bg-[#0047AB]/5 ring-1 ring-[#0047AB]/30";
      }
      return (
        <button
          key={i}
          type="button"
          onClick={() => !revealed && onPick(i)}
          disabled={revealed}
          data-testid={`${testidPrefix}-${i}`}
          className={`text-left border-2 rounded-md px-3.5 py-2.5 transition-all ${cls}`}
        >
          <div className="flex items-start gap-2.5">
            <span
              className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                selected ? "border-[#0047AB] bg-[#0047AB]" : "border-slate-300"
              }`}
            />
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 leading-snug">{o.t}</div>
              {revealed && o.note && (
                <div className={`text-[11px] mt-1 leading-snug ${o.score >= 3 ? "text-emerald-700" : "text-slate-500"}`}>
                  {o.score >= 3 ? "✓ " : selected ? "✗ " : ""}
                  {o.note}
                </div>
              )}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

// Tappable concern chips for the diet-history assessment.
export const ChipRow = ({ chips, selected, onToggle, revealed, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {chips.map((c, i) => {
      const on = selected.has(i);
      let cls = "border-slate-300 bg-white text-slate-700 hover:border-[#0047AB]";
      if (revealed) {
        if (c.flag) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
        else if (on) cls = "border-red-400 bg-red-50 text-red-700 line-through";
        else cls = "border-slate-200 bg-slate-50 text-slate-400";
      } else if (on) {
        cls = "border-[#0047AB] bg-[#0047AB]/10 text-[#0047AB]";
      }
      return (
        <button
          key={i}
          type="button"
          onClick={() => !revealed && onToggle(i)}
          disabled={revealed}
          data-testid={`${testidPrefix}-${i}`}
          className={`text-xs font-semibold rounded-full border-2 px-3 py-1.5 transition-all ${cls}`}
        >
          {c.flag && revealed && "⚑ "}
          {c.t}
        </button>
      );
    })}
  </div>
);

// Shell wrapping each beat: header + body + a single primary action button.
export const BeatShell = ({ Icon, eyebrow, title, subtitle, children, primaryLabel, onPrimary, primaryDisabled, primaryTestId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm fade-up">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-1">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{eyebrow}</span>
    </div>
    <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    {subtitle && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{subtitle}</p>}
    <div className="mt-5 space-y-5">{children}</div>
    <div className="mt-6 flex justify-end">
      <Button
        onClick={onPrimary}
        disabled={primaryDisabled}
        data-testid={primaryTestId}
        className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white"
      >
        {primaryLabel}
      </Button>
    </div>
  </div>
);

export const IntroPanel = ({ caseCount, onStartCase, onStartQuiz }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center max-w-2xl mx-auto">
    <div className="w-14 h-14 rounded-xl bg-[#0047AB]/10 flex items-center justify-center mx-auto">
      <Utensils className="w-7 h-7 text-[#0047AB]" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">
      Nutrition Calculator Trainer
    </h1>
    <p className="text-base text-slate-600 mt-3 leading-relaxed">
      Rapid Cert IV nutrition drills. A <span className="font-semibold text-slate-800">full case workup</span> runs five
      quick stages — assess, calculate RER/MER, choose a diet, plan the feeding, and educate — then grades your maths and
      decisions. Short on time? Hit the <span className="font-semibold text-slate-800">rapid quiz</span> for fast-fire
      RER, MER and portion sums.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
      {["RER 70 × kg^0.75", "MER factors", "kcal → grams / cans / mL", "±10% grading"].map((t) => (
        <span key={t} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600 font-mono-clinical">
          {t}
        </span>
      ))}
    </div>
    <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
      <Button onClick={onStartCase} data-testid="start-drill-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white text-base px-6 py-5">
        <PlayCircle className="w-5 h-5 mr-2" /> Full case workup · {caseCount} cases
      </Button>
      <Button onClick={onStartQuiz} data-testid="start-quiz-btn" variant="outline" className="text-base px-6 py-5 border-[#0047AB]/40 text-[#0047AB] hover:bg-[#0047AB]/5">
        <Zap className="w-5 h-5 mr-2" /> Rapid RER / portion quiz
      </Button>
    </div>
    <FurtherReading topic="nutrition" className="mt-8 text-left" />
  </div>
);

export { GraduationCap };
